import { useState, type KeyboardEvent } from 'react';

import type { BaseSegmentedControlProps } from './types';
import { resolveBaseAppearance } from './theme';
import { cn, hasRenderableNode } from './utils';

function getInitialSegmentValue(
  options: BaseSegmentedControlProps['options'],
  defaultValue?: string,
) {
  if (defaultValue && options?.some((option) => option.value === defaultValue)) {
    const defaultOption = options.find((option) => option.value === defaultValue);
    if (!defaultOption?.disabled) {
      return defaultValue;
    }
  }
  return options?.find((option) => !option.disabled)?.value ?? options?.[0]?.value;
}

export function BaseSegmentedControl({
  options = [],
  value,
  defaultValue,
  appearance,
  className,
  classNames,
  style,
  onValueChange,
  ...controlProps
}: BaseSegmentedControlProps) {
  const theme = resolveBaseAppearance(appearance);
  const [uncontrolledValue, setUncontrolledValue] = useState(() =>
    getInitialSegmentValue(options, defaultValue),
  );
  const isControlled = value !== undefined;
  const selectedValue = isControlled ? value : uncontrolledValue;

  const handleSelect = (nextValue: string, disabled?: boolean) => {
    if (disabled) return;
    if (!isControlled) {
      setUncontrolledValue(nextValue);
    }
    onValueChange?.(nextValue);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
      return;
    }

    const enabledOptions = options.filter((option) => !option.disabled);
    if (enabledOptions.length === 0) return;

    event.preventDefault();
    const currentIndex = Math.max(
      0,
      enabledOptions.findIndex((option) => option.value === selectedValue),
    );
    const nextIndex =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? enabledOptions.length - 1
          : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
            ? (currentIndex - 1 + enabledOptions.length) % enabledOptions.length
            : (currentIndex + 1) % enabledOptions.length;
    const nextOption = enabledOptions[nextIndex];
    const buttons = event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]');
    const target = Array.from(buttons).find(
      (button) => button.dataset.value === nextOption.value,
    );
    handleSelect(nextOption.value);
    target?.focus();
  };

  return (
    <div
      {...controlProps}
      role="radiogroup"
      aria-orientation="horizontal"
      className={cn(theme.slots.segmentedRoot, classNames?.root, className)}
      style={{
        ...theme.styles.segmentedRoot,
        ...style,
      }}
      onKeyDown={handleKeyDown}
    >
      {options.map((option) => {
        const selected = option.value === selectedValue;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            data-value={option.value}
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            disabled={option.disabled}
            className={cn(
              theme.slots.segmentedItem,
              selected && theme.slots.segmentedItemActive,
              option.disabled && theme.slots.segmentedItemDisabled,
              classNames?.item,
            )}
            style={theme.styles.segmentedItem}
            onClick={() => handleSelect(option.value, option.disabled)}
          >
            {hasRenderableNode(option.icon) ? (
              <span className={cn('inline-flex h-4 w-4 shrink-0', classNames?.icon)}>
                {option.icon}
              </span>
            ) : null}
            <span className={cn('min-w-0 truncate', classNames?.label)}>
              {option.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
